import { getSupabaseClient } from '@/integrations/supabase/client';
import { ProductModifierCategory } from '@/types';

export type ProductModifierCategoryInput = Pick<ProductModifierCategory, 'name'>;

type QueryError = { code?: string; message?: string } | null;

/**
 * Org DBs that have not run 35_product_modifier_categories yet have no
 * product_modifier_categories table. Callers keep today's uncategorized list.
 */
const isMissingModifierCategoriesTable = (error: QueryError): boolean => {
  if (!error?.message?.includes('product_modifier_categories')) return false;
  return error.code === '42P01' || error.code === 'PGRST205';
};

const mapCategory = (row: {
  id: string;
  name: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
}): ProductModifierCategory => ({
  id: row.id,
  name: row.name,
  organizationId: row.organization_id,
  createdAt: new Date(row.created_at),
  updatedAt: new Date(row.updated_at),
});

export const productModifierCategoriesService = {
  async getProductModifierCategories(organizationId: string): Promise<ProductModifierCategory[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('product_modifier_categories')
      .select('*')
      .eq('organization_id', organizationId)
      .order('name');

    if (error) {
      if (isMissingModifierCategoriesTable(error)) return [];
      throw error;
    }

    return (data ?? []).map(mapCategory);
  },

  async createProductModifierCategory(category: ProductModifierCategoryInput, organizationId: string) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('product_modifier_categories')
      .insert({
        name: category.name,
        organization_id: organizationId,
      })
      .select()
      .single();

    if (error) throw error;
    return mapCategory(data);
  },

  async updateProductModifierCategory(
    category: ProductModifierCategoryInput & Pick<ProductModifierCategory, 'id'>
  ) {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('product_modifier_categories')
      .update({
        name: category.name,
        updated_at: new Date().toISOString(),
      })
      .eq('id', category.id);

    if (error) throw error;
  },

  async deleteProductModifierCategory(categoryId: string) {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('product_modifier_categories')
      .delete()
      .eq('id', categoryId);

    if (error) throw error;
  },
};
